import CloseIcon from '@mui/icons-material/Close';
import { Button,Dialog,DialogActions,DialogContent,DialogTitle,Skeleton } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { useMutation,useQuery } from '@tanstack/react-query';
import { useContext,useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { UserContext } from '../../../../context/UserContext';
import AttToken from '../../../../helpers/attToken';
import { trackEventMatomo } from '../../../../helpers/matomo';
import Clients,{ ClientType } from '../../../../service/Clients';
import queryClient from '../../../../service/query';
import ServiceTypes from '../../../../service/ServiceTypes';
import { serviceTypeResponse } from '../../../../types/services/types';
import GenericAutoComplete from '../../../GenericAutoComplete';

type props = {
  open: boolean,
  setOpen: Function,
  selectedCustomer: ClientType | null,
  setSelectedCustomer: Function,
};

const services = new ServiceTypes();
const customerService = new Clients();

export default function CustomerTypeAssoci({ open, setOpen, selectedCustomer }: props) {
  const { setChanged } = useContext(UserContext);
  const { t } = useTranslation('translation');
  const [selectedServices, setSelectedServices] = useState<string[]>(selectedCustomer?.tiposServico || []);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['GET_SERVICES'],
    queryFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error('Token not found');
        const body = { ...token, empresa: token.userEmpresa };
        const response: serviceTypeResponse[] = await services.getAllByCompany(body);
        if (!response) throw new Error('No services found');
        return response;
      } catch (error) {
        throw new Error(`Error fetching services: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    },
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    retry: 0
  });

  const { mutate, isPending } = useMutation({
    mutationKey: ['UPDATE_CUSTOMER'],
    mutationFn: async () => {
      trackEventMatomo('Modal associação de clientes', 'click', 'button', 'salva');
      try {
        const token = await AttToken();
        if (!token) throw new Error('Token not found');
        const promise = toast.promise(async () => {
          const response = await customerService.addServiceType({ ...token }, selectedServices, selectedCustomer?.id as string);
          if (!response) throw new Error('Customer not updated');
          return response;
        }, {
          pending: t('toast.saving'),
          success: t('toast.savedSuccess'),
          error: t('toast.errorSaving'),
        }, {
          toastId: 'updateCustomer',
        });
        return promise;
      }
      catch (err) {
        throw new Error(`Error updating customer: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    },
    onSuccess: () => {
      setOpen(false);
      queryClient.resetQueries({ queryKey: ['GET_CUSTOMERS'] });
      setChanged(true);
    }
  });

  const onLoading = isLoading || isFetching || isPending;

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      scroll={'paper'}
    >
      <DialogTitle>{t('places.Associar_Tipos_de_Serviço')}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => setOpen(false)}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers={true}>
        {onLoading
          ? <Skeleton variant='rounded' height={50} />
          : <GenericAutoComplete
            label={t('Services.servicesManagement.kind')}
            options={data?.map((e: serviceTypeResponse) => ({ label: e.nome, value: e.tipoId })) ?? []}
            selectedValues={selectedServices}
            onChange={(values) => setSelectedServices(values ?? [])} />}
        <h1 style={{ marginTop: '10px', backgroundColor: '#ffcc00', color: 'black', padding: '5px', borderRadius: '10px' }}>
          {t('places.click_to_save')}
        </h1>
      </DialogContent>
      <DialogActions>
        <Button disabled={onLoading} onClick={() => mutate()}>{t('save')}</Button>
      </DialogActions>
    </Dialog>
  );
}
